import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  GtmEmailStep,
  GtmEmailTone,
  GtmLeadStage,
  GtmProject,
  UpdateGtmCampaignBuyerRequest,
  UpdateGtmEmailSettingsRequest,
  UpdateGtmLeadRequest,
  UpdateGtmOutreachRequest,
  UpdateGtmSendingSettingsRequest,
} from '@laplace/contracts'
import { trackGtmEvent } from '@/features/gtm/domain/gtm-analytics'
import { apiClient } from '@/shared/api/client'
import { getApiErrorCode } from '@/shared/api/errors'

export class GtmSendRequestError extends Error {
  constructor(readonly code: string | null) {
    super(code ?? 'send_failed')
    this.name = 'GtmSendRequestError'
  }
}

/**
 * A search the server refused: no_capacity (mailboxes have no free room
 * in the next days) or no_balance (the balance does not cover new leads).
 * Shown with `ws_searchRefused_${code}`.
 */
export class GtmSearchRequestError extends Error {
  constructor(readonly code: string | null) {
    super(code ?? 'search_failed')
    this.name = 'GtmSearchRequestError'
  }
}

export function getGtmAnalysisRefetchInterval(analysis: unknown): 3000 | false {
  const status =
    analysis !== null &&
    typeof analysis === 'object' &&
    'run' in analysis &&
    analysis.run !== null &&
    typeof analysis.run === 'object' &&
    'status' in analysis.run
      ? analysis.run.status
      : null
  return status === 'queued' || status === 'running' ? 3000 : false
}

export const gtmWorkspaceKeys = {
  all: (workspaceId: string) => ['gtm-workspace', workspaceId] as const,
  projects: (workspaceId: string) =>
    [...gtmWorkspaceKeys.all(workspaceId), 'projects'] as const,
  project: (workspaceId: string, projectId: string) =>
    [...gtmWorkspaceKeys.all(workspaceId), 'project', projectId] as const,
  overview: (workspaceId: string, projectId: string) =>
    [...gtmWorkspaceKeys.project(workspaceId, projectId), 'overview'] as const,
  analysis: (workspaceId: string, projectId: string) =>
    [...gtmWorkspaceKeys.project(workspaceId, projectId), 'analysis'] as const,
  campaigns: (workspaceId: string, projectId: string) =>
    [...gtmWorkspaceKeys.project(workspaceId, projectId), 'campaigns'] as const,
  emailSettings: (workspaceId: string, projectId: string) =>
    [
      ...gtmWorkspaceKeys.project(workspaceId, projectId),
      'email-settings',
    ] as const,
  outreach: (workspaceId: string, projectId: string) =>
    [...gtmWorkspaceKeys.project(workspaceId, projectId), 'outreach'] as const,
  sendingSettings: (workspaceId: string, projectId: string) =>
    [
      ...gtmWorkspaceKeys.project(workspaceId, projectId),
      'sending-settings',
    ] as const,
  leads: (
    workspaceId: string,
    projectId: string,
    stage?: GtmLeadStage,
    campaignId?: string,
  ) =>
    [
      ...gtmWorkspaceKeys.project(workspaceId, projectId),
      'leads',
      stage ?? 'all',
      campaignId ?? 'all',
    ] as const,
  lead: (workspaceId: string, projectId: string, leadId: string) =>
    [
      ...gtmWorkspaceKeys.project(workspaceId, projectId),
      'lead',
      leadId,
    ] as const,
  sender: (workspaceId: string) =>
    [...gtmWorkspaceKeys.all(workspaceId), 'sender'] as const,
  usage: (workspaceId: string) =>
    [...gtmWorkspaceKeys.all(workspaceId), 'usage'] as const,
  /** Organization-wide figures: deliberately not keyed by project. */
  usageOverview: (workspaceId: string) =>
    [...gtmWorkspaceKeys.all(workspaceId), 'usage-overview'] as const,
  conversations: (workspaceId: string, projectId: string) =>
    [
      ...gtmWorkspaceKeys.project(workspaceId, projectId),
      'conversations',
    ] as const,
}

export function useGtmProjectsQuery(workspaceId: string) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.projects(workspaceId),
    queryFn: () => apiClient.gtmWorkspace.projects(workspaceId),
    refetchInterval: 30_000,
  })
}

export function useCreateGtmProjectMutation(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (url: string) =>
      apiClient.gtmWorkspace.createProject(workspaceId, url),
    onSuccess: ({ project, search }) => {
      trackGtmEvent('gtm_project_created', {}, project.id)
      queryClient.setQueryData<Array<GtmProject>>(
        gtmWorkspaceKeys.projects(workspaceId),
        (current = []) =>
          current.some((item) => item.id === project.id)
            ? current.map((item) => (item.id === project.id ? project : item))
            : [...current, project],
      )
      queryClient.setQueryData(
        [...gtmWorkspaceKeys.project(workspaceId, project.id), 'search'],
        search,
      )
    },
  })
}

export function useGtmAnalysisQuery(workspaceId: string, projectId: string) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.analysis(workspaceId, projectId),
    queryFn: () => apiClient.gtmWorkspace.analysis(workspaceId, projectId),
    enabled: Boolean(projectId),
    refetchInterval: (query) => getGtmAnalysisRefetchInterval(query.state.data),
  })
}

export function useGtmSenderQuery(workspaceId: string) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.sender(workspaceId),
    queryFn: () => apiClient.gtmWorkspace.sender(workspaceId),
    refetchInterval: 30_000,
  })
}

export function useGtmUsageQuery(workspaceId: string) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.usage(workspaceId),
    queryFn: () => apiClient.gtmWorkspace.usage(workspaceId),
    refetchInterval: 30_000,
  })
}

/**
 * Email capacity and balance for the sidebar. Sends invalidate everything
 * under the workspace key, this included; it also refreshes on its own.
 */
export function useGtmUsageOverviewQuery(workspaceId: string) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.usageOverview(workspaceId),
    queryFn: () => apiClient.gtmWorkspace.usageOverview(workspaceId),
    refetchInterval: 60_000,
  })
}

/**
 * Inbox Refresh: asks the server to check the mailboxes for replies now,
 * then reloads everything shown for the workspace.
 */
export function useRefreshGtmInboxMutation(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.gtmWorkspace.refreshInbox(workspaceId),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.all(workspaceId),
      }),
  })
}

export function useGtmConversationsQuery(
  workspaceId: string,
  projectId: string,
) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.conversations(workspaceId, projectId),
    queryFn: () => apiClient.gtmWorkspace.conversations(workspaceId, projectId),
    enabled: Boolean(projectId),
    refetchInterval: 30_000,
  })
}

export function useSendGtmMessageMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (messageId: string) => {
      try {
        return await apiClient.gtmWorkspace.sendMessage(
          workspaceId,
          projectId,
          messageId,
        )
      } catch (error) {
        throw new GtmSendRequestError(await getApiErrorCode(error))
      }
    },
    onSuccess: (_result, messageId) => {
      trackGtmEvent('gtm_send_requested', {}, messageId)
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.all(workspaceId),
      }),
  })
}

export function useCreateGtmDraftMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      leadId,
      subject,
      body,
    }: {
      leadId: string
      subject: string
      body: string
    }) =>
      apiClient.gtmWorkspace.createDraft(workspaceId, projectId, leadId, {
        subject,
        body,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.all(workspaceId),
      }),
  })
}

export function useUpdateGtmDraftMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      messageId,
      subject,
      body,
    }: {
      messageId: string
      subject: string
      body: string
    }) =>
      apiClient.gtmWorkspace.updateDraft(workspaceId, projectId, messageId, {
        subject,
        body,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.all(workspaceId),
      }),
  })
}

export function useGtmOverviewQuery(workspaceId: string, projectId: string) {
  return useQuery(
    queryOptions({
      queryKey: gtmWorkspaceKeys.overview(workspaceId, projectId),
      queryFn: () => apiClient.gtmWorkspace.overview(workspaceId, projectId),
      enabled: Boolean(projectId),
    }),
  )
}

export function useGtmEmailSettingsQuery(
  workspaceId: string,
  projectId: string,
) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.emailSettings(workspaceId, projectId),
    queryFn: () => apiClient.gtmWorkspace.emailSettings(workspaceId, projectId),
    enabled: Boolean(projectId),
  })
}

/**
 * An email in a tone (LP-289). Kept for the session: switching back to a
 * tone shows it at once; the server keeps it too, so no model call either.
 * Outside the project's key, so saving a tone does not ask for it again.
 */
export function useGtmEmailPreviewQuery(
  workspaceId: string,
  projectId: string,
  step: GtmEmailStep,
  tone: GtmEmailTone,
) {
  return useQuery({
    queryKey: ['gtm-email-preview', workspaceId, projectId, step, tone],
    queryFn: () =>
      apiClient.gtmWorkspace.emailPreview(workspaceId, projectId, {
        step,
        tone,
      }),
    enabled: Boolean(projectId),
    staleTime: Infinity,
    retry: false,
  })
}

/**
 * Saves the email style (LP-277). Drafts change in the background, so the
 * whole project is read again: leads carry their drafts and hints.
 */
export function useUpdateGtmEmailSettingsMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (change: UpdateGtmEmailSettingsRequest) =>
      apiClient.gtmWorkspace.updateEmailSettings(
        workspaceId,
        projectId,
        change,
      ),
    onSuccess: (settings) => {
      queryClient.setQueryData(
        gtmWorkspaceKeys.emailSettings(workspaceId, projectId),
        settings,
      )
      return queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.project(workspaceId, projectId),
      })
    },
  })
}

export function useGtmSendingSettingsQuery(
  workspaceId: string,
  projectId: string,
) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.sendingSettings(workspaceId, projectId),
    queryFn: () =>
      apiClient.gtmWorkspace.sendingSettings(workspaceId, projectId),
    enabled: Boolean(projectId),
  })
}

/** Saves the sending settings (LP-276); planned emails may move. */
export function useUpdateGtmSendingSettingsMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (change: UpdateGtmSendingSettingsRequest) =>
      apiClient.gtmWorkspace.updateSendingSettings(
        workspaceId,
        projectId,
        change,
      ),
    onSuccess: (settings) => {
      queryClient.setQueryData(
        gtmWorkspaceKeys.sendingSettings(workspaceId, projectId),
        settings,
      )
      return queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.project(workspaceId, projectId),
      })
    },
  })
}

/** Today's sending plan; refreshed as the scheduler works (LP-275). */
export function useGtmOutreachPlanQuery(
  workspaceId: string,
  projectId: string,
) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.outreach(workspaceId, projectId),
    queryFn: () => apiClient.gtmWorkspace.outreach(workspaceId, projectId),
    enabled: Boolean(projectId),
    refetchInterval: 60_000,
  })
}

/** Start, pause or resume outreach. */
export function useUpdateGtmOutreachMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (change: UpdateGtmOutreachRequest) => {
      try {
        return await apiClient.gtmWorkspace.updateOutreach(
          workspaceId,
          projectId,
          change,
        )
      } catch (error) {
        // no_mailbox, said in words.
        throw new GtmSendRequestError(await getApiErrorCode(error))
      }
    },
    onSuccess: (plan) => {
      queryClient.setQueryData(
        gtmWorkspaceKeys.outreach(workspaceId, projectId),
        plan,
      )
      return queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.projects(workspaceId),
      })
    },
  })
}

export function useGtmCampaignsQuery(workspaceId: string, projectId: string) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.campaigns(workspaceId, projectId),
    queryFn: () => apiClient.gtmWorkspace.campaigns(workspaceId, projectId),
    enabled: Boolean(projectId),
  })
}

/** The client's own product type and buyer roles for a campaign (LP-286). */
export function useUpdateGtmCampaignBuyerMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      campaignId,
      change,
    }: {
      campaignId: string
      change: UpdateGtmCampaignBuyerRequest
    }) =>
      apiClient.gtmWorkspace.updateCampaignBuyer(
        workspaceId,
        projectId,
        campaignId,
        change,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.campaigns(workspaceId, projectId),
      }),
  })
}

export function useGtmLeadsQuery(
  workspaceId: string,
  projectId: string,
  filter: { stage?: GtmLeadStage; campaignId?: string },
) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.leads(
      workspaceId,
      projectId,
      filter.stage,
      filter.campaignId,
    ),
    queryFn: () => apiClient.gtmWorkspace.leads(workspaceId, projectId, filter),
    enabled: Boolean(projectId),
  })
}

export function useGtmLeadQuery(
  workspaceId: string,
  projectId: string,
  leadId: string | undefined,
) {
  return useQuery({
    queryKey: gtmWorkspaceKeys.lead(workspaceId, projectId, leadId ?? ''),
    queryFn: () =>
      apiClient.gtmWorkspace.lead(workspaceId, projectId, leadId ?? ''),
    enabled: Boolean(projectId && leadId),
    refetchInterval: 30_000,
  })
}

/** A stage change touches the lead, the lists and the counts: refresh them all. */
export function useUpdateGtmLeadMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      leadId,
      body,
    }: {
      leadId: string
      body: UpdateGtmLeadRequest
    }) =>
      apiClient.gtmWorkspace.updateLead(workspaceId, projectId, leadId, body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.all(workspaceId),
      }),
  })
}

const SEARCH_POLL_MS = 3000

/** The workspace's latest lead search, polled while it runs. */
export function useGtmLatestSearchQuery(
  workspaceId: string,
  projectId: string,
) {
  return useQuery({
    queryKey: [...gtmWorkspaceKeys.project(workspaceId, projectId), 'search'],
    queryFn: () => apiClient.gtmWorkspace.latestSearch(workspaceId, projectId),
    enabled: Boolean(projectId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'queued' || status === 'running'
        ? SEARCH_POLL_MS
        : false
    },
  })
}

export function useStartGtmSearchMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      try {
        return await apiClient.gtmWorkspace.startSearch(workspaceId, projectId)
      } catch (error) {
        throw new GtmSearchRequestError(await getApiErrorCode(error))
      }
    },
    onSuccess: (search) => {
      trackGtmEvent('gtm_search_started', {}, search.runId)
      queryClient.setQueryData(
        [...gtmWorkspaceKeys.project(workspaceId, projectId), 'search'],
        search,
      )
    },
  })
}

export function useRecordGtmReplyMutation(
  workspaceId: string,
  projectId: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, text }: { leadId: string; text: string }) =>
      apiClient.gtmWorkspace.recordReply(workspaceId, projectId, leadId, text),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: gtmWorkspaceKeys.all(workspaceId),
      }),
  })
}
